<template>
    <transition name="betslip-compact__animation">
        <BetSlipCompact
            v-if="this.selections.length > 0 || isInTicketNegotiation"
            :coefficient="totalCoefficient"
            :stake="rawStake"
            :potentialWin="maxPotentialWin"
            :hasLiveEvents="hasLiveEvents"
            :numberOfEvents="this.selections.length"
            :totalNumberOfSelectedCombinations="totalNumberOfSelectedCombinations"
            :isSimple="isSimple"
            @expand="openBetslipPreview"
            :isSuperBonusVisible="isSuperBonusVisible"
            :isEligible="superBonus.isEligible"
            :isUnlocked="superBonus.isUnlocked"
            :isBoosted="isAnyOddBoosted"
            :selections="superBonus.numberOfSelections.eligible"
            :selectionsForFirstBonus="superBonusNumberOfSelectionsMinimum"
            :selectionsNeededForNext="superBonusPercentage.selectionsNeededForNext"
            :percentage="superBonusPercentage.current"
            :nextPercentage="superBonusPercentage.next"
            :maximumPercentage="superBonusPercentage.maximumPercentage"
            :notEligibleReason="superBonus.notEligibleReason"
            :isSocialOnly="isSocialOnly"
        />
    </transition>
</template>

<script>
    import {
        mapGetters,
        mapActions,
    } from 'vuex';
    import { Country } from '@superbet-group/web.types';

    import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
    import { isSuperBonusVisibleOnCompactBetSlip } from '@utils/superBonus/displayLogic';

    import BetSlipCompact from './BetSlipCompact';
    import { isBoostedOdd } from '@sportOfferFederation/boostedOdds/utils/conflicts';
    import { isPriceBoostOdd } from '@src/federations/sportOffer/price-boost/utils';

    export default {
        props: {
            isInTicketNegotiation: {
                type: Boolean,
                required: true,
            },
        },
        created() {
            this.setDefaultStake();
        },
        computed: {
            ...mapGetters('data/country', {
                serverConfig: 'config',
            }),
            ...mapGetters('ui/modal', [
                'modal',
            ]),
            ...mapGetters('ui/sportOffer/betSlip', [
                'selections',
                'rawStake',
                'payout',
                'totalCoefficient',
                'totalNumberOfSelectedCombinations',
                'isSimple',
                'isSuperBonusEnabled',
                'superBonusNumberOfSelectionsMinimum',
                'superBonus',
                'superBonusPercentage',
            ]),
            ...mapGetters('ui/sportOffer/experiments', [
                'isShowingSuperBonusForAnySelectionCount',
            ]),
            hasLiveEvents() {
                return this.selections.some((s) => s.event.isOngoing());
            },
            maxPotentialWin() {
                return Math.min(this.payout.final, this.serverConfig.betSlip.maxWin);
            },
            isSinglesOnly() {
                return (this.selections ?? []).some((s) => isBoostedOdd(s?.odd));
            },
            isSocialOnly() {
                return this.serverConfig.countrySpecific.country === Country.BrSocial;
            },
            isSuperBonusVisible() {
                return (
                    this.isSuperBonusEnabled
                    && !this.isSinglesOnly
                    && isSuperBonusVisibleOnCompactBetSlip(
                        this.superBonus.numberOfSelections,
                        this.isSimple,
                        this.isShowingSuperBonusForAnySelectionCount ? 1 : undefined,
                    )
                );
            },
            betSlipModalCode() {
                return modalCodes.mobileBetSlip;
            },
            isBetslipExpanded() {
                return this.modal?.code === this.betSlipModalCode;
            },
            isAnyOddBoosted() {
                return this.selections.some((s) => isPriceBoostOdd(s?.odd));
            },
        },
        watch: {
            // When in ticket negotiation mode, we want to show the user the betslip
            // that contains the ticket negotiation prompt, so that they are aware of it.
            // Both of the two handlers are necessary to handle different paths that this flow
            // can be accessed.
            isInTicketNegotiation: {
                handler(isInNegotiation, wasInNegotiation) {
                    if (isInNegotiation && !wasInNegotiation && !this.isBetslipExpanded) {
                        this.openBetslipPreview();
                    }
                },
                immediate: true,
            },
            isBetslipExpanded: {
                handler(isExpanded) {
                    if (!isExpanded && this.isInTicketNegotiation) {
                        this.openBetslipPreview();
                    }
                },
                immediate: true,
            },
        },
        methods: {
            openBetslipPreview() {
                this.setModal({
                    code: this.betSlipModalCode,
                    data: {
                        hasSystems: this.hasSystems,
                    },
                });
            },
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            ...mapActions('ui/sportOffer/betSlip', [
                'setDefaultStake',
            ]),
        },
        components: {
            BetSlipCompact,
        },
    };
</script>
